import { NodeProps } from '@xyflow/react'

import i18n from 'modules/core/lib/i18n'

import { conditionBranchId } from 'modules/workflows/lib/flow/ids'
import { WorkflowsExpression } from 'modules/workflows/lib/types'

import { EdgeUI, NodeUI, NodeUICondition, NodeUIType } from './types'

export const hasTopConnection = (type: NodeUIType): boolean =>
  type !== 'conditionIf' && type !== 'conditionElse'

export const hasBottomConnection = (type: NodeUIType): boolean => type !== 'condition'

export const nodeIsValid = (node: NodeUI, edges: EdgeUI[]): boolean => {
  // const sourceEdges = edges.filter((edge) => edge.source === node.id)
  const targetEdges = edges.filter((edge) => edge.target === node.id)

  return targetEdges.length > 0 || node.type === 'trigger'
}

const createDefaultExpression = (): WorkflowsExpression => {
  return {
    root: {
      operator: 'and',
      expressions: [],
    },
  }
}

export const generateUniqueLabel = (existingComparisons: Array<{ label: string }>) => {
  const existingLabels = existingComparisons.map((c) => c.label)
  let counter = 1
  let newLabel = `comparison_${counter}`
  while (existingLabels.includes(newLabel)) {
    counter++
    newLabel = `comparison_${counter}`
  }
  return newLabel
}

export const createNodesAndEdges = (
  nodeId: string,
  type: NodeUIType,
  position: { x: number; y: number }
): { nodes: NodeUI[]; edges?: EdgeUI[] } => {
  if (type === 'condition') {
    // For condition nodes, create three nodes: condition, conditionIf, and conditionElse
    const conditionNodeId = nodeId
    const defaultLabel = generateUniqueLabel([])
    const ifNodeId = conditionBranchId(nodeId, `if-${defaultLabel}`)
    const elseNodeId = conditionBranchId(nodeId, 'else')
    const defaultExpression = createDefaultExpression()

    const nodes: NodeUI[] = [
      {
        id: conditionNodeId,
        type: 'condition',
        position: position,
        data: {
          step: 1,
          title: i18n.t('workflows.node_type.condition.default.title'),
          description: i18n.t('workflows.node_type.condition.default.description'),
          isValid: true,
          suppressInitialValidation: true,
          comparisons: [],
          expression: defaultExpression,
        },
      },
      {
        id: ifNodeId,
        type: 'conditionIf',
        position: { x: position.x, y: position.y },
        data: {
          step: 2,
          title: i18n.t('workflows.node_type.condition_if.title'),
          description: i18n.t('workflows.node_type.condition_if.description'),
          isValid: true,
          comparison: {
            label: defaultLabel,
            priority: 1,
            expression: defaultExpression,
          },
          expression: defaultExpression,
        },
      },
      {
        id: elseNodeId,
        type: 'conditionElse',
        position: { x: position.x, y: position.y },
        data: {
          step: 3,
          title: i18n.t('workflows.node_type.condition_else.title'),
          description: i18n.t('workflows.node_type.condition_else.description'),
          isValid: true,
        },
      },
    ]

    const edges: EdgeUI[] = [
      {
        id: `${conditionNodeId}-${ifNodeId}`,
        source: conditionNodeId,
        target: ifNodeId,
        type: 'default',
      },
      {
        id: `${conditionNodeId}-${elseNodeId}`,
        source: conditionNodeId,
        target: elseNodeId,
        type: 'default',
      },
    ]

    return { nodes, edges }
  }

  if (type == 'task') {
    return {
      nodes: [
        {
          id: nodeId,
          type: type,
          position: position,
          data: {
            step: 1,
            title: i18n.t('workflows.node_type.task.default.title'),
            description: i18n.t('workflows.node_type.task.default.description'),
            isValid: true,
            payload: {},
            handler_name: '',
            action_group: 'task',
          },
        },
      ],
    }
  }

  if (type == 'approvals') {
    return {
      nodes: [
        {
          id: nodeId,
          type: type,
          position: position,
          data: {
            step: 1,
            title: i18n.t('workflows.node_type.approval.default.title'),
            description: i18n.t('workflows.node_type.approval.default.description'),
            isValid: true,
            payload: {},
            handler_name: '',
            action_group: 'approvals',
          },
        },
      ],
    }
  }

  throw new Error(`Can not create nodes and edges for node type: ${type}`)
}

// Helper to check if a specific field in an expression is invalid
export const getFieldErrors = (expression: WorkflowsExpression, comparisonIndex: number) => {
  const expr = expression.root.expressions?.[comparisonIndex]
  if (!expr) return { variable: false, operator: false, value: false }

  return {
    variable: !expr.left?.name,
    operator: !expr.operator,
    value: !expr.right?.value || expr.right.value === '',
  }
}

// Validation helper to check if an expression is complete
const isExpressionValid = (expression: WorkflowsExpression) => {
  if (!expression.root.expressions?.length) return false

  return expression.root.expressions.every((_, index) => {
    const errors = getFieldErrors(expression, index)
    return !errors.variable && !errors.operator && !errors.value
  })
}

// Validation helper to check if a condition node is valid
export function isNodeUIConditionValid(node: NodeUICondition): boolean
export function isNodeUIConditionValid(props: NodeProps<NodeUICondition>): boolean
export function isNodeUIConditionValid(
  nodeOrProps: NodeUICondition | NodeProps<NodeUICondition>
): boolean {
  return (
    nodeOrProps.data.comparisons.length > 0 &&
    nodeOrProps.data.comparisons.every((comparison) => isExpressionValid(comparison.expression))
  )
}
